import Offer from "domain/offer";
import { DeliveryMethod } from "domain/deliveryMethod";
import { ContractType } from "domain/contract";
import { toContractType, OfferTypeEnum } from "domain/offerType";
import Merchant from "domain/merchant";
import { Offering } from "features/settings/offerings/offering";
import Commodity from "domain/commodity";
import PhysicalLocation from "domain/physicalLocation";
import * as _ from "lodash";
import { DeliveryMethodsByContractType } from "./deliveryMethodService";
import { convertToCentralTime, isDateBetween } from "common/dateTimeHelpers";
import { getCommodityBalanceType } from "domain/commodityBalance";
import { getNextDayForGivenDate } from "common/formatters";
import { isAgrisErp } from "domain/erpSystem";
import DeliveryPeriod from "domain/deliveryPeriod";

function isMatch(customerWillSell: Nullish<boolean>, offerType: Nullish<OfferTypeEnum>, offering: Offering, commodity: Nullish<Commodity>, location?: Nullish<PhysicalLocation>): boolean {
    if (customerWillSell == null || offerType == null) return false;
    return offering.location?.id === location?.id
        && offering.commodity?.id === commodity?.id
        && (offering.offerTypes.includes(offerType) || offering.contractTypes.includes(toContractType(offerType)))
        && (customerWillSell ? offering.allowSelling : offering.allowBuying);
}

function getDeliveryMethodsForOffering(contractType: ContractType, offering: Offering, allowedDeliveryMethods: DeliveryMethodsByContractType, isCustomer: boolean): DeliveryMethod[] {
    return isCustomer
        ? _.intersection(offering.deliveryMethods, allowedDeliveryMethods[contractType])
        : allowedDeliveryMethods[contractType];
}

export function resolveDeliveryMethods(
    forOffer: NullishPartial<Offer>,
    allowedDeliveryMethods: DeliveryMethodsByContractType,
    offerings: Offering[],
    isCustomer: boolean
): DeliveryMethod[] {
    if (forOffer?.type == null || allowedDeliveryMethods == null) return [];

    const asContractType = toContractType(forOffer.type);

    const dms = (forOffer.location == null || !isCustomer)
        ? allowedDeliveryMethods[asContractType]
        : _.uniq(offerings
            .filter(o => isMatch(forOffer.customerWillSell, forOffer.type, o, forOffer.commodity, forOffer.location))
            .flatMap(o => getDeliveryMethodsForOffering(asContractType, o, allowedDeliveryMethods, isCustomer)));

    return _.sortBy(dms);
}

export function filterOutDeliveryMethodsInvalidForPeriod(deliveryMethods: DeliveryMethod[], selectedDeliveryMethod: Nullish<DeliveryMethod>, selectedDeliveryPeriod: Nullish<DeliveryPeriod>, merchant: Merchant): DeliveryMethod[] {
    return deliveryMethods.filter(dm => dm === selectedDeliveryMethod || isDeliveryMethodValidForPeriod(dm, selectedDeliveryPeriod, merchant))
}

export function isDeliveryMethodValidForPeriod(deliveryMethod: Nullish<DeliveryMethod>, deliveryPeriod: Nullish<DeliveryPeriod>, merchant: Merchant) {
    if (isAgrisErp(merchant.erpSystem)) {
        const nowInCentralTime = convertToCentralTime(new Date());
        return getCommodityBalanceType(deliveryMethod) == null || deliveryPeriod == null || isDateBetween(nowInCentralTime, deliveryPeriod.start, getNextDayForGivenDate(deliveryPeriod.end))
    } else {
        return true;
    }
}

export function resolveDefaultDeliveryMethod(
    forOffer: NullishPartial<Offer>,
    merchant: Merchant,
    allowedDeliveryMethods: DeliveryMethodsByContractType,
    offerings: Offering[],
    isCustomer: boolean
): DeliveryMethod {
    const deliveryMethods = resolveDeliveryMethods(forOffer, allowedDeliveryMethods, offerings, isCustomer);

    const defaultForDeliveryPeriod = forOffer.deliveryPeriod?.defaultDeliveryMethod;
    const defaultForContractType = merchant?.contractDeliveryMethodMappings
        ?.find(cm => cm.contractType === toContractType(forOffer.type))?.deliveryMethod;

    const defaultDeliveryMethod = defaultForDeliveryPeriod ?? defaultForContractType;

    return deliveryMethods.find(m => defaultDeliveryMethod === m)
        ?? deliveryMethods.find(m => isCustomer && forOffer.trader?.defaultDeliveryMethod === m)
        ?? deliveryMethods.find(m => forOffer.deliveryMethod === m)
        ?? deliveryMethods[0];
}